<template>
    <loading v-if="isLoadingGroupData"></loading>
    <div class="template template--sidebar" v-else>

        <sidebar-teacher
            :user="loggedInUser"
            :group="currentGroup"
            :loadingUserProgress="isLoadingUserProgress"
            :isDemo="isDemo"
            :groupTerm="groupTerm"></sidebar-teacher>

        <div class="page-wrap" :class="{ 'adjust-for-mini-sidebar': isInspectingStudent }">
            <transition name="view-transition" mode="out-in" @after-leave="afterPageViewLeave">
                <router-view class="page-view"
                    :key="viewKey"
                    :loading="isLoadingUserProgress"
                    :user="viewingUser"
                    :group="currentGroup"
                    :isInspectingStudent="isInspectingStudent"
                    :isInspecting="true"
                    :ignoreProgress="ignoreProgress"
                    :isDemo="isDemo"></router-view>
            </transition>
        </div>

    </div>
</template>


<script>

import SidebarTeacher from 'js/components/sidebar/SidebarTeacher.vue'

import Loading from 'js/components/elements/Loading.vue'
import notify from 'js/components/notifications/notificationsMixin'
import track from 'js/helpers/analytics'

import { fetchGroupTerm } from 'js/vuex/actions'
import { getGroupIdForCurrentUser, getGroup, getCurrentGroupTermForGroup } from 'js/vuex/getters'
import api from 'js/api/resources'
import { transform, logreject } from 'js/vuex/helpers'
import { canTeach, isDemo } from 'js/api/auth'

export default {
    name: 'GroupManager',
    components: { SidebarTeacher, Loading },
    mixins: [ notify ],

    data: function() {
        return {
            isLoadingGroupData: true,
            isLoadingUserProgress: false,
        }
    },

    vuex: {
        getters: {
            getGroupIdForCurrentUser,
            getGroup,
            getCurrentGroupTermForGroup,
        },
        actions: {
            fetchGroup: ({dispatch}, id) => {
                return api.getGroup(id)
                    .then( response => {

                        let result = transform(response.body)
                        dispatch('ADD_TERMS', result.terms || [])
                        dispatch('ADD_PERIODS', result.periods || [])
                        dispatch('ADD_GROUPS', result.groups || []);
                        dispatch('ADD_GROUP_TERMS', result.groupterms || [])
                        dispatch('ADD_GROUP_PERIODS', result.groupperiods || [])

                    })
                    .catch(error => logreject('Could not fetch students', error))
            },
            fetchGroupTerm,
            fetchStudentsForGroup: ({dispatch}, id) => {

                return api.getStudentsForGroup(id)
                    .then( response => {

                        let result = transform(response.body)
                        let students = result.users || []

                        dispatch('ADD_USERS', students);
                        dispatch('SET_STUDENTS_FOR_GROUP', students.map( s => s.id), id)

                        return students

                    })
                    .catch(error => logreject('Could not fetch students', error))

            },
            showGroupArchivedModal: ({ dispatch }, user ) => {
                dispatch('SHOW_MODAL', 'GroupArchived', { user: user })
            },
        },
    },

    computed: {

        viewKey: function() {

            let key = this.$route.name.split('.').slice(0,2).join('.')

            if ( this.$route.params.groupId !=  null ) key += '-group-' + this.$route.params.groupId
            if ( this.$route.params.periodId !=  null ) key += '-period-' + this.$route.params.periodId
            if ( this.$route.params.assignmentId !=  null ) key += '-assignment-' + this.$route.params.assignmentId
            if ( this.$route.params.studentId !=  null ) key += '-student-' + this.$route.params.studentId

            return key
        },

        currentGroup: function() {
            return this.getGroup( this.$route.params.groupId )
        },

        groupTermId: function() {

            if ( this.$route.params.groupTermId != null ) {
                return this.$route.params.groupTermId
            }

            if ( this.$route.params.periodId != null ) {
                return this.currentGroup.groupTerms
                    .map( id => this.$store.state.groups.terms[id] )
                    .find( gt => gt.groupPeriods.indexOf(this.$route.params.periodId) !== -1 )
                    .id
            }

            return this.currentGroup == null ? null : (this.currentGroup.currentGroupTerm == null ? null : this.currentGroup.currentGroupTerm[0])

        },

        groupTerm: function() {
            return this.$store.state.groups.terms[this.groupTermId]
        },

        loggedInUser: function() {
            return this.$store.state.users.current
        },

        viewingUser: function() {
            return this.$route.params.studentId != null
                ? this.$store.state.users.storage[ this.$route.params.studentId ]
                : this.loggedInUser
        },

        isInspectingStudent: function() {
            return this.viewingUser == null ? false : this.loggedInUser.id !== this.viewingUser.id
        },

        ignoreProgress: function() {
            return !this.isInspectingStudent
        },

        isDemo: function() {
            return isDemo(this.loggedInUser)
        }

    },

    methods: {
        loadInitialData: function(route) {

            // check if the group is already loaded
            // (Should have been included in user essentials)
            const loadGroup = this.currentGroup == null
                ? this.fetchGroup(route.params.groupId)
                : Promise.resolve()

            this.isLoadingGroupData = true

            return loadGroup.then( () => {

                return Promise.all([
                    this.loadStudentsData(),
                    this.loadTermData(this.groupTermId, route.params.studentId, route.params.periodId)
                ])
            })
                .catch(() => this.$router.replace({ name: 'root' }) )
                .then(() => this.isLoadingGroupData = false)

        },
        loadTermData: function(groupTermId, studentId, periodId) {

            return this.fetchGroupTerm(groupTermId, studentId, periodId)
                .catch( err => {
                    if ( err.status === 404 ) {
                        this.$warn('Er zijn nog geen opdrachten voor jou gevonden, probeer het later nog eens.')
                    } else {
                        track.exception('Can\'t load term', err)
                    }
                    return Promise.reject(err)
                })

        },
        loadStudentsData: function() {

            return this.fetchStudentsForGroup( this.currentGroup.id )
                .catch( err => {
                    track.exception('Can\'t load students for group ', err)
                    this.$error('Kan leerlingen niet laden.')
                    return Promise.reject(err)
                })

        },
        afterPageViewLeave: function () {
            document.getElementById('main-view').scrollTop = 0
        },
    },

    created: function() {

        if ( ! canTeach(this.loggedInUser) ) {
            this.$router.replace({ name: 'root' })
            return
        }

        this.loadInitialData( this.$route )
          .then(() => {
              // route to overview if no students are registered yet
              if (this.$route.name === 'students.overview' && this.$route.params.groupTermId == null && this.currentGroup.students.length === 0) {
                  this.$router.replace({ name: 'group.overview' })
              }
          })
    },

    watch: {
        // not immediate because initial data loaded on created
        groupTermId: function(newVal, oldVal) {
            if (newVal !== oldVal) {
                // TODO: CACHE?
                this.isLoadingUserProgress = true
                this.loadTermData(this.groupTermId, this.$route.params.studentId)
                  .catch(() => this.$router.replace({ name: 'root' }) )
                  .then(() => this.isLoadingUserProgress = false)
            }
        },

        '$route': function(to, from) {

            // TODO: load only user progres, not term data
            const studentSwitch = to.params.studentId != null && to.params.studentId != from.params.studentId

            if ( studentSwitch ) {
                this.isLoadingUserProgress = true
                this.loadTermData(this.groupTermId, to.params.studentId)
                    .catch(() => this.$router.replace({ name: 'root' }) )
                    .then(() => this.isLoadingUserProgress = false)
            }

        }
    },

}
</script>


<style lang="scss">

    @import 'global';

</style>



// WEBPACK FOOTER //
// GroupManager.vue?3cd1393a